/**
 * What has gone out, and what is still being written — 17 Sep 2026.
 *
 * The share register's mail log, applied to the newsletter: one row per send,
 * drafts in the same list, and the detail behind a click rather than on the
 * page. The old view put a table of rates next to a composer next to a
 * suppression list; this is the landing view and it answers one question.
 *
 * ## Drafts belong here
 *
 * A draft has no recipients, no figures and no send date, so its row shows when
 * it was last saved and opens the composer rather than a report. Keeping it in
 * the same list is the point — "what have we written to these people" is one
 * question, and half an answer in two places is how a half-written letter gets
 * forgotten.
 *
 * ## The rates are measured against what was delivered
 *
 * A message that bounced was never in front of anybody, and counting it in the
 * denominator makes a campaign look worse the more dead addresses the list
 * carries — which reads as a content problem when it is a list problem.
 */
import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Suppressions } from './Suppressions'
import { SendTimeline } from './SendTimeline'
import { ResendNonOpeners, type ResendCounts } from './ResendNonOpeners'
import { callFunction } from '@/shared/firebase/callFunction'
import { newsletterFromFile } from './importNewsletter'
import { formatDate } from '@/shared/util/dates'

import { nf } from '@/shared/util/numbers'
import { AUDIENCE_LABELS } from './audienceLabels'
import {
  dailyLimitNote,
  deleteDraft,
  isUnsent,
  refreshDraftHtml,
  subscribeToNewsletters,
  type Newsletter,
  loadSendLinks,
  type SendLink,
} from './repository/newsletterRepository'
import './Newsletter.css'

const STATUS: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'mail__pill--muted' },
  scheduled: { label: 'Scheduled', cls: 'mail__pill--busy' },
  sending: { label: 'Sending', cls: 'mail__pill--busy' },
  partial: { label: 'Partial', cls: 'mail__pill--warn' },
  finished: { label: 'Sent', cls: 'mail__pill--ok' },
  'finished-with-errors': { label: 'Sent, with errors', cls: 'mail__pill--warn' },
}

/** Every audience the send named, in the words the compose screen used. */
function audienceOf(n: Newsletter): string {
  const keys = n.audiences?.length ? n.audiences : n.audience ? [n.audience] : []
  if (keys.length === 0) return 'No audience'
  const where = [...(n.reach?.countries ?? []), ...(n.reach?.languages ?? [])].join(', ')
  return keys.map((k) => AUDIENCE_LABELS[k] ?? k).join(' + ') + (where ? ` · ${where}` : '')
}

/** A `Date` from the repository, as the rest of the app writes a date. */
const day = (d: Date) => formatDate(d.toISOString().slice(0, 10))

/**
 * Sortable columns — 14 Oct 2026. Each reads one comparable value off a row;
 * drafts have no figures, so they sort as -1 below every send.
 */
type SortKey = 'when' | 'name' | 'subject' | 'audience' | 'recipients' | 'opened' | 'clicked' | 'status'

const whenOf = (n: Newsletter) => (isUnsent(n) ? n.createdAt : n.startedAt ?? n.createdAt)?.getTime() ?? 0
const rate = (part: number | undefined, n: Newsletter) =>
  isUnsent(n) || !n.delivered ? -1 : (part || 0) / n.delivered

const SORT_VALUE: Record<SortKey, (n: Newsletter) => string | number> = {
  when: whenOf,
  name: (n) => (n.name || n.subject || '').toLowerCase(),
  subject: (n) => (n.subject || '').toLowerCase(),
  audience: (n) => audienceOf(n).toLowerCase(),
  recipients: (n) => (isUnsent(n) ? -1 : n.sent || 0),
  opened: (n) => rate(n.opened, n),
  clicked: (n) => rate(n.clicked, n),
  status: (n) => STATUS[n.status]?.label ?? n.status,
}

const COLUMNS: { key: SortKey; label: string; num?: boolean }[] = [
  { key: 'when', label: 'Date' },
  { key: 'name', label: 'Internal name' },
  { key: 'subject', label: 'Subject' },
  { key: 'audience', label: 'Audience' },
  { key: 'recipients', label: 'Recipients', num: true },
  { key: 'opened', label: 'Opened', num: true },
  { key: 'clicked', label: 'Clicked', num: true },
  { key: 'status', label: 'Status' },
]

const pct = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—'

export function NewsletterLog({
  onCompose,
  onOpenDraft,
}: {
  onCompose: () => void
  onOpenDraft: (draft: Newsletter) => void
}) {
  const [rows, setRows] = useState<Newsletter[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Newsletter | null>(null)
  /**
   * A tab rather than a nav destination — 17 Sep 2026.
   *
   * The suppression list is the other half of "who are we reaching": it is read
   * when a count looks wrong, which is while looking at the sends. A top-level
   * menu item put a page nobody visits next to the ones they do.
   */
  const [tab, setTab] = useState<'sends' | 'suppressed'>('sends')

  useEffect(
    // Every identity: a send can reach app users and PRO companies at once.
    () => subscribeToNewsletters('all', setRows, (e) => setError(e.message), 300),
    [],
  )

  // Stored drafts re-rendered with the current design, once per visit. The
  // write comes back through the subscription, so `rows` settles after it.
  const [refreshed, setRefreshed] = useState(false)
  useEffect(() => {
    if (!rows || refreshed) return
    setRefreshed(true)
    void refreshDraftHtml(
      rows.filter((n) => n.audience !== 'shareholders' && n.audience !== 'issuance-subscribers'),
    ).catch(() => undefined)
  }, [rows, refreshed])

  // The share register's own mail shares this collection and has its own log.
  const list = (rows ?? []).filter(
    (n) => n.audience !== 'shareholders' && n.audience !== 'issuance-subscribers',
  )
  const sent = list.filter((n) => !isUnsent(n))

  // Newest first until somebody picks a column; a second click reverses it.
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'when', dir: -1 })
  const sorted = useMemo(() => {
    const v = SORT_VALUE[sort.key]
    return [...list].sort((a, b) => {
      const x = v(a)
      const y = v(b)
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
    })
    // `list` is rebuilt every render from `rows`; `rows` is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort])
  const sortBy = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'when' ? -1 : 1 }))
  const totalRecipients = sent.reduce((n, x) => n + (x.delivered || x.sent || 0), 0)

  return (
    <div className="mail nlog">
      <div className="mail__head">
        <div>
          <h2 className="mail__title">Newsletters</h2>
          <p className="mail__lede">
            Everything that has gone out, and everything still being written.
          </p>
        </div>
        {tab === 'sends' && (
          <div className="nlog__headacts">
            <label className="btn btn--ghost" title="Open a newsletter saved as a .json file, as a new unsaved draft">
              Import from file
              <input
                type="file"
                accept="application/json,.json"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file) return
                  file
                    .text()
                    .then((t) => onOpenDraft(newsletterFromFile(t)))
                    .catch((err: unknown) => setError(`Could not import ${file.name}: ${err instanceof Error ? err.message : String(err)}`))
                }}
              />
            </label>
            <button className="btn btn--primary" onClick={onCompose}>
              New newsletter
            </button>
          </div>
        )}
      </div>

      <div className="nlog__tabs">
        <button className="chip" aria-pressed={tab === 'sends'} onClick={() => setTab('sends')}>
          Newsletters
        </button>
        <button
          className="chip"
          aria-pressed={tab === 'suppressed'}
          onClick={() => setTab('suppressed')}
        >
          Suppressed
        </button>
      </div>

      {tab === 'suppressed' && <Suppressions />}

      {error && <p className="recform__error">{error}</p>}

      {tab === 'sends' && (
      <div className="nlog__stats">
        <span>
          <strong>{nf(sent.length)}</strong> sent
        </span>
        <span>
          <strong>{nf(totalRecipients)}</strong> delivered
        </span>
        <span>
          <strong>{nf(list.filter(isUnsent).length)}</strong> drafts and scheduled
        </span>
      </div>

      )}

      {tab === 'sends' &&
        (rows === null ? (
        <p className="empty-note">Loading…</p>
      ) : list.length === 0 ? (
        <p className="empty-note">Nothing has been written yet.</p>
      ) : (
        <div className="mail__table" role="table" aria-label="Newsletters">
          <div className="mail__row mail__row--head nlog__row" role="row">
            {COLUMNS.map((c) => (
              <span
                key={c.key}
                role="columnheader"
                aria-sort={sort.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
              >
                <button
                  type="button"
                  className={`nlog__sort${c.num ? ' nlog__sort--num' : ''}`}
                  onClick={() => sortBy(c.key)}
                >
                  {c.label}
                  <span className="nlog__arrow" aria-hidden="true">
                    {sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : ''}
                  </span>
                </button>
              </span>
            ))}
          </div>
          {sorted.map((n) => {
            const unsent = isUnsent(n)
            const waiting = dailyLimitNote(n)
            const state = waiting
              ? { label: 'Spreading over days', cls: 'mail__pill--warn' }
              : STATUS[n.status] ?? STATUS.finished!
            const delivered = n.delivered || 0
            const when = unsent
              ? n.createdAt
                ? `Saved ${day(n.createdAt)}`
                : 'Not sent'
              : n.startedAt
                ? day(n.startedAt)
                : '—'
            const audience = `${audienceOf(n)} · ${n.kind === 'service' ? 'Service' : 'Marketing'}`
            return (
              <button
                className="mail__row mail__row--click nlog__row"
                role="row"
                key={n.id}
                onClick={() => (unsent ? onOpenDraft(n) : setOpen(n))}
              >
                {/* One line per row: every cell clips with an ellipsis, and the
                    full text is in its tooltip. */}
                <span role="cell" className="nlog__cell" title={`${when}${n.sentBy || n.savedBy ? ` · ${n.sentBy || n.savedBy}` : ''}`}>
                  {when}
                </span>
                <span role="cell" className="nlog__cell nlog__name" title={n.name}>
                  {n.name || n.subject || 'Untitled'}
                </span>
                <span role="cell" className="nlog__cell" title={n.subject}>
                  {n.subject || '—'}
                </span>
                <span role="cell" className="nlog__cell nlog__muted" title={audience}>
                  {audience}
                </span>
                <span role="cell" className="nlog__num">{unsent ? '—' : nf(n.sent || 0)}</span>
                <span role="cell" className="nlog__num">{unsent ? '—' : pct(n.opened || 0, delivered)}</span>
                <span role="cell" className="nlog__num">{unsent ? '—' : pct(n.clicked || 0, delivered)}</span>
                <span role="cell" className="mail__last">
                  <span className={`mail__pill ${state.cls}`} title={waiting ?? undefined}>
                    {state.label}
                  </span>
                  {unsent && (
                    <span
                      className="mail__discard"
                      role="button"
                      tabIndex={0}
                      title="Discard"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (window.confirm('Discard this draft?')) void deleteDraft(n.id)
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.stopPropagation()
                        e.preventDefault()
                        if (window.confirm('Discard this draft?')) void deleteDraft(n.id)
                      }}
                    >
                      Discard
                    </span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
        ))}

      {open && <SendDetail n={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

/**
 * One send, in detail.
 *
 * The message as it went out, and what became of it. Per-person delivery is
 * deliberately not here: opens and clicks are counted per campaign and never
 * per reader — see the note in `resendWebhook.ts`. The share register's mail
 * names recipients because a cap table is a list of forty; a newsletter's is
 * forty thousand people who did not agree to be watched individually.
 */
function SendDetail({ n, onClose }: { n: Newsletter; onClose: () => void }) {
  const delivered = n.delivered || 0
  const [links, setLinks] = useState<SendLink[] | null>(null)

  // Loaded when the row is opened, not with the list — see `loadSendLinks`.
  useEffect(() => {
    let live = true
    loadSendLinks(n.id)
      .then((rows) => live && setLinks(rows))
      .catch(() => live && setLinks([]))
    return () => {
      live = false
    }
  }, [n.id])

  return (
    <Modal
      title={n.name || n.subject || 'Untitled'}
      subtitle={`${audienceOf(n)} · ${n.startedAt ? day(n.startedAt) : ''}`}
      onClose={onClose}
      width={820}
      footer={
        <button className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="mail__detail">
        <div className="mail__stats">
          <span>
            <strong>{nf(n.sent || 0)}</strong> sent
          </span>
          <span>
            <strong>{nf(delivered)}</strong> delivered
          </span>
          <span>
            <strong>{pct(n.opened || 0, delivered)}</strong> opened
          </span>
          <span>
            <strong>{pct(n.clicked || 0, delivered)}</strong> clicked
          </span>
          {(n.bounced || 0) > 0 && (
            <span>
              <strong className="mail__bad">{nf(n.bounced || 0)}</strong> bounced
            </span>
          )}
          {(n.unsubscribed || 0) > 0 && (
            <span
              title={
                (n.unsubscribedAll || 0) > 0
                  ? `${n.unsubscribedAll} of them switched everything off`
                  : 'All of them kept something — they narrowed what they receive rather than leaving'
              }
            >
              <strong className="mail__bad">{nf(n.unsubscribed || 0)}</strong> unsubscribed
              {(n.unsubscribedAll || 0) > 0 && ` · ${nf(n.unsubscribedAll || 0)} entirely`}
            </span>
          )}
        </div>

        <div className="card-label">Over time</div>
        <SendTimeline sendId={n.id} unsubscribedTotal={n.unsubscribed || 0} />

        <ResendNonOpeners
          state={{
            status: n.status,
            finishedAt: n.finishedAt ?? null,
            subject: n.subject,
            resendOf: n.resendOf ?? null,
            resentAs: n.resentAs ?? null,
            resentAt: n.resentAt ?? null,
          }}
          note="Apple Mail reports every message as opened, so its readers are not counted here."
          count={() =>
            callFunction<ResendCounts>('crmResendNonOpeners', { method: 'POST', body: { sendId: n.id, dryRun: true } })
          }
          send={(subject) =>
            callFunction<ResendCounts>('crmResendNonOpeners', { method: 'POST', body: { sendId: n.id, subject } }).then(
              (r) => r.willSend,
            )
          }
        />

        {/*
          Which link, rather than how many links — 21 Sep 2026.

          A click rate says somebody pressed something. This says what, which is
          the only version of the number that changes what goes in the next
          newsletter.
        */}
        {links !== null && links.length > 0 && (
          <>
            <div className="card-label">What they pressed</div>
            <div className="nlog__links" role="table">
              <div className="nlog__linkrow nlog__linkrow--head" role="row">
                <span role="columnheader">Destination</span>
                <span role="columnheader" title="Messages that produced at least one press">
                  Readers
                </span>
                <span role="columnheader" title="Every press, including repeats">
                  Presses
                </span>
              </div>
              {links.map((l) => (
                <div className="nlog__linkrow" role="row" key={l.id}>
                  <span role="cell" className="nlog__linkurl" title={l.url}>
                    {l.url}
                  </span>
                  <span role="cell">{pct(l.reach, delivered)}</span>
                  <span role="cell">{nf(l.clicks)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="card-label">The message, as it was sent</div>
        {/* Our own rendered HTML, from the send record. */}
        <div className="nlog__preview" dangerouslySetInnerHTML={{ __html: n.html }} />

        <p className="micro">
          Opens and clicks are counted for the campaign and never for a named reader — the rates
          answer &ldquo;did this work&rdquo;, which is the question being asked. The link table is
          the same: a destination and two numbers, with nothing in it that could be traced back to
          a person.
        </p>
      </div>
    </Modal>
  )
}
